import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  Phone,
  MapPin,
  Clock,
  Hospital,
  Briefcase,
  Droplet,
  HeartPulse,
  Repeat,
  Users,
  Building2,
  AlertTriangle,
  Accessibility,
} from 'lucide-react';

const CANONICAL_PATH = '/sugar-land-tx-medical-transportation';

const SUGAR_LAND_FAQS = [
  {
    question: 'What Sugar Land neighborhoods does MoveraCare serve?',
    answer:
      'MoveraCare provides non-emergency medical transportation throughout Sugar Land and surrounding Fort Bend County communities, including First Colony, Greatwood, New Territory, Telfair, Riverstone, Colony Meadows, Sugar Creek, Sugar Lakes, Settlers Way, Commonwealth, and the areas around Sugar Land Town Square. Service is scheduled subject to availability.',
  },
  {
    question: 'Do you transport patients to Methodist Sugar Land Hospital or OakBend Medical Center?',
    answer:
      'Yes. MoveraCare provides non-emergency wheelchair and ambulatory transportation for Sugar Land-area patients traveling to appointments at hospitals and clinics throughout Fort Bend County, including scheduled visits to major Sugar Land-area medical facilities, subject to scheduling availability.',
  },
  {
    question: 'Do you offer recurring dialysis transportation in Sugar Land?',
    answer:
      'Yes. Standing weekly schedules are supported for Sugar Land dialysis patients — Monday/Wednesday/Friday, Tuesday/Thursday/Saturday, or other recurring plans coordinated with the treating dialysis center.',
  },
  {
    question: 'Can Sugar Land hospitals and skilled nursing facilities request discharge transportation?',
    answer:
      'Yes. Sugar Land discharge planners, case managers, and social workers can request non-emergency discharge transportation from a Sugar Land hospital or SNF to home, rehabilitation, or assisted living. Same-day and scheduled discharges are supported for medically stable patients.',
  },
  {
    question: 'How do I book a wheelchair-accessible ride in Sugar Land?',
    answer:
      'Call dispatch at (832) 925-2128 or use the online Book a Ride form. For a family member or care coordinator arranging transportation on behalf of a Sugar Land patient, the Request Patient Transportation form captures the details we need.',
  },
  {
    question: "Do you accept Workers' Compensation authorizations for Sugar Land injured workers?",
    answer:
      "Yes. MoveraCare accepts authorized non-emergency transportation for Sugar Land-area injured workers traveling to Workers' Compensation-approved medical, therapy, IME, FCE, and occupational medicine appointments. Authorization is determined by the applicable adjuster, case manager, TPA, or payer.",
  },
  {
    question: 'How far in advance should Sugar Land riders schedule?',
    answer:
      'We recommend at least 24–48 hours notice for routine Sugar Land trips. Recurring runs (dialysis, therapy, rehabilitation) should be set up in advance as a standing schedule. Same-day requests may be considered when vehicle and driver availability allows.',
  },
];

const SUGAR_LAND_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'MedicalBusiness',
  '@id': 'https://www.moveracaremobility.com/sugar-land-tx-medical-transportation#business',
  name: 'MoveraCare Mobility — Sugar Land, TX Non-Emergency Medical Transportation',
  legalName: 'MoveraCare Services LLC',
  description:
    "Richmond, TX-based non-emergency medical transportation provider serving Sugar Land and Fort Bend County — wheelchair and ambulatory transportation for medical appointments, dialysis, hospital discharge, rehabilitation, and Workers' Compensation.",
  url: 'https://www.moveracaremobility.com/sugar-land-tx-medical-transportation',
  telephone: '+1-832-925-2128',
  email: 'dispatch@moveracaremobility.com',
  areaServed: [
    { '@type': 'City', name: 'Sugar Land, TX' },
    { '@type': 'AdministrativeArea', name: 'Fort Bend County, Texas' },
  ],
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Richmond',
    addressRegion: 'TX',
    addressCountry: 'US',
  },
  openingHoursSpecification: [
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'], opens: '06:00', closes: '19:00' },
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Sunday'], opens: '12:00', closes: '17:00' },
  ],
};

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: SUGAR_LAND_FAQS.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  })),
};

const SERVICE_TYPES = [
  { icon: Accessibility, title: 'Wheelchair Transportation', desc: 'Wheelchair-accessible transportation for Sugar Land patients traveling to medical destinations.', to: '/services/wheelchair-transportation' },
  { icon: Users, title: 'Ambulatory Transportation', desc: 'Comfortable transport for medically stable ambulatory patients throughout Sugar Land and Fort Bend County.', to: '/services/ambulatory-transportation' },
  { icon: Hospital, title: 'Hospital Discharge Transportation', desc: 'Same-day and scheduled discharge transportation from Sugar Land hospitals to home, SNF, rehab, or assisted living.', to: '/services/hospital-discharge' },
  { icon: Droplet, title: 'Dialysis Transportation', desc: 'Recurring standing schedules for Sugar Land dialysis patients aligned to treatment days.', to: '/services/dialysis-transportation' },
  { icon: HeartPulse, title: 'Therapy & Rehabilitation Transportation', desc: 'Physical therapy, occupational therapy, and rehabilitation transportation for Sugar Land patients.', to: '/services/therapy-rehabilitation-transportation' },
  { icon: Briefcase, title: "Workers' Compensation Transportation", desc: 'Authorized transportation for Sugar Land injured workers to approved medical, therapy, and IME destinations.', to: '/healthcare-partners/workers-compensation' },
  { icon: Repeat, title: 'Recurring Medical Transportation', desc: 'Standing NEMT schedules for Sugar Land patients with ongoing treatment plans.', to: '/services/recurring-medical-transportation' },
  { icon: Clock, title: 'Wait-and-Return Transportation', desc: 'Driver-waiting service for shorter Sugar Land appointments when arranged in advance.', to: '/services' },
];

const SUGAR_LAND_AREAS = [
  'First Colony',
  'Greatwood',
  'New Territory',
  'Telfair',
  'Riverstone',
  'Colony Meadows',
  'Sugar Creek',
  'Sugar Lakes',
  'Settlers Way',
  'Commonwealth',
  'Avalon',
  'Sugar Mill',
  'Chelsea Harbour',
  'Sugar Land Town Square',
  'Missouri City (adjacent)',
  'Stafford (adjacent)',
  'Rosenberg (adjacent)',
  'Richmond (adjacent)',
];

const NEIGHBORHOOD_ANCHORS = [
  { name: 'Sugar Land hospitals & specialty clinics', desc: 'Sugar Land is served by a growing network of hospitals, specialty clinics, and outpatient centers. MoveraCare coordinates non-emergency wheelchair and ambulatory transportation for Sugar Land patients traveling to appointments at facilities throughout the city and Fort Bend County.' },
  { name: 'Sugar Land dialysis centers', desc: 'Recurring weekly transportation for Sugar Land dialysis patients on standing 3-day-per-week schedules coordinated with the treating dialysis center.' },
  { name: 'Sugar Land discharge coordination', desc: 'Same-day and scheduled hospital and SNF discharge transportation from Sugar Land facilities to home, rehabilitation, or assisted living for medically stable patients.' },
  { name: 'Occupational health & Workers’ Comp destinations', desc: 'Non-emergency transportation for authorized injured worker appointments across Sugar Land, including physical therapy, IME, FCE, and occupational medicine.' },
];

const SugarLandMedicalTransportation = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="sugar-land-page">
      <Seo
        title="Wheelchair &amp; Medical Transportation Sugar Land TX | MoveraCare Mobility"
        description="Wheelchair transportation Sugar Land TX and non-emergency medical transportation across Fort Bend County and Greater Houston. Based in Richmond, TX. Call 832-925-2128."
        path={CANONICAL_PATH}
        schema={[SUGAR_LAND_SCHEMA, FAQ_SCHEMA]}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Service Areas', url: '/services' },
          { name: 'Sugar Land, TX', url: CANONICAL_PATH },
        ]}
      />

      {/* Hero */}
      <section className="py-16 md:py-20 bg-[#004C97]" data-testid="sugar-land-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="text-sm text-white/80 mb-6" data-testid="breadcrumbs">
            <ol className="flex flex-wrap items-center gap-1">
              <li><Link to="/" className="hover:text-[#F9A825]">Home</Link></li>
              <li className="text-white/50">/</li>
              <li><Link to="/services" className="hover:text-[#F9A825]">Service Areas</Link></li>
              <li className="text-white/50">/</li>
              <li className="text-[#F9A825] font-semibold">Sugar Land, TX</li>
            </ol>
          </nav>
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-[#F9A825] text-[#004C97] px-4 py-1.5 rounded-full text-sm font-bold mb-6">
              <MapPin className="w-4 h-4" />
              SUGAR LAND · FORT BEND COUNTY, TX
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Non-Emergency Medical &amp; Wheelchair Transportation in Sugar Land, Texas
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              MoveraCare Mobility is based in Richmond, TX and provides professional wheelchair and ambulatory non-emergency medical transportation — including wheelchair transportation in Sugar Land — for patients, families, healthcare facilities, case managers, and Workers&apos; Compensation coordinators across Sugar Land, Fort Bend County, and Greater Houston.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <Link to="/book">
                <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold text-lg px-8 shadow-lg" data-testid="sugar-land-cta-book">
                  Book a Ride
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/request-patient-transportation">
                <Button size="lg" className="bg-white text-[#004C97] hover:bg-gray-100 font-semibold text-lg px-8 shadow-lg" data-testid="sugar-land-cta-refer">
                  Refer a Patient
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:+18329252128">
                <Button size="lg" variant="outline" className="border-2 border-[#F9A825] text-[#F9A825] hover:bg-[#F9A825] hover:text-[#004C97] font-semibold text-lg px-8" data-testid="sugar-land-cta-call">
                  <Phone className="mr-2 w-5 h-5" />
                  Call Dispatch (832) 925-2128
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sugar Land NEMT Overview */}
      <section className="py-14 bg-white" data-testid="sugar-land-overview">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">SUGAR LAND NEMT OVERVIEW</p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-6 leading-tight">
            Non-Emergency Medical Transportation for Sugar Land Patients &amp; Families
          </h2>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
            <p>
              Sugar Land is one of the fastest-growing communities in Fort Bend County, home to a diverse population, a steadily expanding healthcare footprint, and an aging demographic that increasingly relies on scheduled medical transportation. From routine specialty visits to standing dialysis schedules and hospital discharges to home or a skilled nursing facility, dependable non-emergency transportation is a core part of daily care in Sugar Land.
            </p>
            <p>
              MoveraCare Mobility supports Sugar Land patients with professional wheelchair-accessible and ambulatory non-emergency medical transportation. Our team operates out of the Fort Bend County service area, making Sugar Land part of our core coverage. Services include wheelchair transportation using wheelchair-accessible vehicles with proper securement, comfortable ambulatory transport, recurring dialysis and therapy transportation, hospital and SNF discharge coordination, and authorized Workers&apos; Compensation transportation.
            </p>
            <p>
              Every ride is provided for medically stable passengers and scheduled subject to availability. Extended-distance assignments may require advance authorization. MoveraCare is a non-emergency medical transportation provider and does not provide ambulance, 911, stretcher ambulance, oxygen therapy, or clinical monitoring services. For medical emergencies, call 911.
            </p>
          </div>
        </div>
      </section>

      {/* Services offered */}
      <section className="py-14 bg-gray-50" data-testid="sugar-land-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">SERVICES OFFERED IN SUGAR LAND</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] leading-tight">
              Sugar Land Non-Emergency Medical Transportation Services
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICE_TYPES.map((s) => {
              const Icon = s.icon;
              return (
                <Link key={s.title} to={s.to} className="group" data-testid={`sugar-land-service-${s.title.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}>
                  <Card className="h-full border-2 hover:border-[#F9A825] transition-all">
                    <CardHeader>
                      <div className="w-11 h-11 rounded-lg bg-[#F9A825] flex items-center justify-center mb-2">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <CardTitle className="text-[#004C97] text-base leading-tight">{s.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 leading-relaxed mb-2">{s.desc}</p>
                      <span className="text-sm font-semibold text-[#F9A825] group-hover:text-[#E08E1E]">Explore →</span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Wheelchair & Ambulatory */}
      <section className="py-14 bg-white" data-testid="sugar-land-wheelchair-ambulatory">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WHEELCHAIR TRANSPORTATION</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] mb-4 leading-tight">
                Sugar Land Wheelchair-Accessible Transportation
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                For Sugar Land patients who need to remain seated in a manual or power wheelchair during transport, MoveraCare operates wheelchair-accessible vehicles with proper wheelchair securement. Drivers assist passengers during transport. We coordinate with family members, home health aides, and facility staff on pickup logistics — door-to-door or curb-to-curb assistance is coordinated at booking. Please share the wheelchair type, weight, and any equipment needs (including oxygen) when booking so dispatch can confirm we can safely serve the trip.
              </p>
              <Link to="/services/wheelchair-transportation" className="inline-flex items-center text-sm font-semibold text-[#F9A825] hover:text-[#E08E1E]">
                Learn about Wheelchair Transportation <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
            <div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">AMBULATORY TRANSPORTATION</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] mb-4 leading-tight">
                Sugar Land Ambulatory Medical Transportation
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Many Sugar Land patients can walk to and from the vehicle independently or with limited assistance, but still benefit from a professional driver and scheduled coordination. MoveraCare provides comfortable ambulatory transportation for Sugar Land medical, dental, therapy, and specialty appointments, with round-trip and wait-and-return options available.
              </p>
              <Link to="/services/ambulatory-transportation" className="inline-flex items-center text-sm font-semibold text-[#F9A825] hover:text-[#E08E1E]">
                Learn about Ambulatory Transportation <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* For Sugar Land Healthcare Organizations */}
      <section className="py-14 bg-[#004C97]" data-testid="sugar-land-healthcare-org">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">FOR SUGAR LAND HEALTHCARE ORGANIZATIONS</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
              Transportation for Hospitals, SNFs, Dialysis Centers &amp; Case Managers
            </h2>
            <p className="text-gray-200 mt-3 max-w-3xl">
              MoveraCare works directly with Sugar Land hospitals, skilled nursing facilities, rehabilitation centers, dialysis centers, case managers, discharge planners, and social workers to coordinate reliable non-emergency medical transportation.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {NEIGHBORHOOD_ANCHORS.map((a) => (
              <div key={a.name} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
                <h3 className="text-white font-semibold text-lg mb-2">{a.name}</h3>
                <p className="text-gray-200 text-sm leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link to="/healthcare-partners">
              <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold" data-testid="sugar-land-cta-healthcare-partners">
                Healthcare Partner Programs
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/healthcare-partners/hospital-discharge">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#004C97] font-semibold">
                Hospital Discharge
              </Button>
            </Link>
            <Link to="/healthcare-partners/dialysis-centers">
              <Button size="lg" variant="outline" className="border-2 border-[#F9A825] text-[#F9A825] hover:bg-[#F9A825] hover:text-[#004C97] font-semibold">
                Dialysis Centers
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Workers' Compensation for Sugar Land */}
      <section className="py-14 bg-white" data-testid="sugar-land-workers-comp">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div>
              <div className="w-14 h-14 rounded-lg bg-[#F9A825] flex items-center justify-center mb-3">
                <Briefcase className="w-7 h-7 text-white" />
              </div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WORKERS&apos; COMPENSATION</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] leading-tight">
                Sugar Land Workers&apos; Compensation Transportation
              </h2>
            </div>
            <div className="lg:col-span-2">
              <p className="text-gray-700 leading-relaxed mb-4">
                MoveraCare provides non-emergency transportation for Sugar Land-area injured workers attending Workers&apos; Compensation-authorized medical, therapy, IME, FCE, occupational medicine, and diagnostic appointments. We coordinate with adjusters, nurse case managers, TPAs, and ancillary service coordinators. Authorization determinations are made by the applicable payer, adjuster, case manager, or TPA — being a network vendor does not automatically guarantee that a trip is covered or payable.
              </p>
              <Link to="/healthcare-partners/workers-compensation" className="inline-flex items-center text-sm font-semibold text-[#F9A825] hover:text-[#E08E1E]" data-testid="sugar-land-cta-workers-comp">
                Workers&apos; Compensation Transportation Details <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Served */}
      <section className="py-14 bg-gray-50" data-testid="sugar-land-areas-served">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">AREAS SERVED</p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4 leading-tight">
            Sugar Land Neighborhoods &amp; Fort Bend County Coverage
          </h2>
          <p className="text-gray-700 mb-6 max-w-3xl">
            MoveraCare&apos;s core operating area is Fort Bend County, which means Sugar Land sits at the center of our service coverage. Service is scheduled subject to availability. Extended-distance assignments may require advance authorization.
          </p>
          <div className="flex flex-wrap gap-2">
            {SUGAR_LAND_AREAS.map((a) => (
              <span key={a} className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm text-[#004C97] font-medium">
                {a}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose MoveraCare */}
      <section className="py-14 bg-white" data-testid="sugar-land-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WHY CHOOSE MOVERACARE</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97]">Built for Sugar Land Healthcare Transportation</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: MapPin, title: 'Fort Bend County Core Coverage', desc: 'Sugar Land is inside MoveraCare’s primary service area, supporting reliable local coordination.' },
              { icon: Accessibility, title: 'Wheelchair-Accessible Vehicles', desc: 'Wheelchair-accessible vehicles with proper wheelchair securement. Drivers assist passengers during transport.' },
              { icon: Users, title: 'Driver Assistance', desc: 'Drivers assist passengers during transport. Mobility and equipment needs are confirmed at booking.' },
              { icon: Repeat, title: 'Standing Schedules', desc: 'Reliable recurring transportation for Sugar Land dialysis, therapy, and treatment programs.' },
              { icon: Hospital, title: 'Discharge-Ready Dispatch', desc: 'Responsive same-day and scheduled hospital and SNF discharge coordination.' },
              { icon: Building2, title: 'Credentialing-Ready', desc: 'Licensing, insurance, and vendor documentation available for Sugar Land facility procurement teams.' },
            ].map((w) => {
              const Icon = w.icon;
              return (
                <div key={w.title} className="flex items-start gap-3 p-5 border-2 border-gray-100 rounded-xl hover:border-[#F9A825] transition-all">
                  <div className="w-10 h-10 rounded-lg bg-[#F9A825]/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-[#F9A825]" />
                  </div>
                  <div>
                    <h3 className="text-[#004C97] font-semibold mb-1 leading-tight">{w.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{w.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-gray-50" data-testid="sugar-land-faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">SUGAR LAND FAQ</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97]">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {SUGAR_LAND_FAQS.map((f) => (
              <details key={f.question} className="bg-white border-2 border-gray-100 rounded-lg p-5 group">
                <summary className="font-semibold text-[#004C97] cursor-pointer flex items-center justify-between">
                  <span>{f.question}</span>
                  <span className="text-[#F9A825] transition-transform group-open:rotate-90">→</span>
                </summary>
                <p className="mt-3 text-sm text-gray-700 leading-relaxed">{f.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 bg-[#F9A825]" data-testid="sugar-land-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4">
            Request Sugar Land Medical Transportation
          </h2>
          <p className="text-lg text-[#004C97]/85 mb-8">
            Book a Sugar Land wheelchair or ambulatory ride, refer a patient, or reach dispatch directly.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center">
            <Link to="/book">
              <Button size="lg" className="bg-[#004C97] text-white hover:bg-[#003D7A] font-semibold text-lg px-8 py-6 shadow-lg" data-testid="sugar-land-final-book">
                Book a Ride
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/request-patient-transportation">
              <Button size="lg" className="bg-white text-[#004C97] hover:bg-gray-100 font-semibold text-lg px-8 py-6 shadow-lg" data-testid="sugar-land-final-refer">
                Refer a Patient
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+18329252128">
              <Button size="lg" variant="outline" className="border-2 border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white font-semibold text-lg px-8 py-6" data-testid="sugar-land-final-call">
                <Phone className="mr-2 w-5 h-5" />
                (832) 925-2128
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Non-emergency disclaimer */}
      <section className="py-6 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-100 border-l-4 border-gray-400 rounded p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-700 leading-relaxed">
              MoveraCare provides <strong>non-emergency medical transportation</strong> for medically stable passengers in Sugar Land and Fort Bend County. We do not provide ambulance, 911, emergency response, stretcher ambulance, oxygen therapy, or clinical monitoring services. <strong>Not for medical emergencies. Call 911 in an emergency.</strong>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SugarLandMedicalTransportation;
